import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CMSService } from "../../../../services/cms";

interface ServicesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingService: CMSService | null;
  setEditingService: React.Dispatch<React.SetStateAction<CMSService | null>>;
}

const ServicesDialog: React.FC<ServicesDialogProps> = ({
  open,
  onClose,
  onSave,
  editingService,
  setEditingService,
}) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof CMSService, value: any) => {
    if (!editingService) return;
    setEditingService({
      ...editingService,
      [field]: value,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "95%", sm: "90%", md: "80%" },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
        {editingService?.id
          ? t("admin.contentManagement.editService")
          : t("admin.contentManagement.addService")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("admin.contentManagement.fields.iconName")}
            fullWidth
            value={editingService?.icon_name || ""}
            onChange={(e) => handleChange("icon_name", e.target.value)}
            helperText={t("admin.contentManagement.fields.iconHelperText")}
          />
          <TextField
            label={t("admin.contentManagement.fields.title")}
            fullWidth
            value={editingService?.title || ""}
            onChange={(e) => handleChange("title", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.description")}
            fullWidth
            multiline
            rows={3}
            value={editingService?.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label={t("admin.contentManagement.fields.gradientStartColor")}
              fullWidth
              type="color"
              value={editingService?.gradient_start || "#00d4ff"}
              onChange={(e) => handleChange("gradient_start", e.target.value)}
            />
            <TextField
              label={t("admin.contentManagement.fields.gradientEndColor")}
              fullWidth
              type="color"
              value={editingService?.gradient_end || "#0099cc"}
              onChange={(e) => handleChange("gradient_end", e.target.value)}
            />
          </Box>
          <TextField
            label={t("admin.contentManagement.fields.shadowColor")}
            fullWidth
            value={editingService?.shadow_color || ""}
            onChange={(e) => handleChange("shadow_color", e.target.value)}
            helperText={t(
              "admin.contentManagement.fields.shadowColorHelperText"
            )}
          />
          <TextField
            label={t("admin.contentManagement.fields.displayOrder")}
            fullWidth
            type="number"
            value={editingService?.display_order || 0}
            onChange={(e) => handleChange("display_order", parseInt(e.target.value))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editingService?.is_active ?? true}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label={t("admin.contentManagement.active")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          {t("admin.contentManagement.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          sx={{
            background: "linear-gradient(135deg, #ff0080 0%, #cc0066 100%)",
          }}
        >
          {t("admin.contentManagement.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServicesDialog;